import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContextWeb } from "../context/AuthContextWeb";
import { apiRequest } from "../lib/apiClient";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseWeb";
import { ensureFirebaseLoginWeb } from "../firebase/ensureFirebaseLoginWeb";
import {
  BsHouseDoor,
  BsColumnsGap,
  BsLayers,
  BsArrowsAngleExpand,
  BsCalendar3,
  BsCalendarCheck,
  BsCashCoin,
  BsTools,
  BsCarFront,
  BsHeart,
  BsDoorOpen,
  BsDroplet,
  BsLightning,
  BsThermometerHalf,
  BsSnow,
  BsShieldCheck,
  BsToggle2On,
  BsEnvelope,
  BsPinMap,
  BsChatDots,
  BsShare,
  BsArrowLeft,
  BsPencil,
  BsEyeSlash,
  BsTrash,
} from "react-icons/bs";

/* ── helpers ── */
function asCurrency(value) {
  if (typeof value !== "number") return null;
  return new Intl.NumberFormat("en-NP", {
    style: "currency",
    currency: "NPR",
    maximumFractionDigits: 0,
  }).format(value);
}

function fmt(v) {
  return v !== null && v !== undefined && v !== "" ? String(v) : "—";
}

function humanize(val) {
  if (!val) return "—";
  return val
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function fmtBool(v) {
  return v === true ? "Yes" : v === false ? "No" : "—";
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

const EAGER_THUMB_COUNT = 4;

export default function PropertyDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [property, setProperty] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  const { user, token } = useContext(AuthContextWeb);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [shareFeedback, setShareFeedback] = useState("");

  const handleMessageOwner = async () => {
    setMsgLoading(true);
    setMsgError("");
    try {
      const ownerId = String(property.ownerId);
      const renterId = String(user.userId);
      await ensureFirebaseLoginWeb(token, renterId);
      const listingId = String(id);
      const conversationId = `listing:${listingId}__owner:${ownerId}__renter:${renterId}`;
      await setDoc(
        doc(db, "conversations", conversationId),
        {
          conversationId,
          listingId,
          listingTitle: property.title,
          ownerId,
          renterId,
          ownerName: "",
          renterName: user?.name || "",
          participantUids: [ownerId, renterId],
          participants: {
            [ownerId]: { role: "OWNER", name: "" },
            [renterId]: { role: "RENTER", name: user?.name || "" },
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastMessage: "",
          deletedForOwner: false,
          deletedForRenter: false,
          deletedFor: { [ownerId]: false, [renterId]: false },
        },
        { merge: true },
      );
      navigate(`/app/inbox?c=${conversationId}`);
    } catch (err) {
      setMsgError("Could not open chat. Please try again.");
    } finally {
      setMsgLoading(false);
    }
  };
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleShareProperty = async () => {
    const shareUrl =
      typeof window !== "undefined"
        ? window.location.href
        : `/app/properties/${id}`;

    const shareData = {
      title: property?.title || "Rental Highway property",
      text: "Check out this property on Rental Highway",
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShareFeedback("Property link shared.");
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setShareFeedback("Property link copied.");
        return;
      }

      window.prompt("Copy property link:", shareUrl);
    } catch (err) {
      if (err?.name === "AbortError") return;
      setShareFeedback("Could not share right now.");
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    apiRequest(`/properties/${id}`)
      .then((data) => {
        if (!active) return;
        let amenities = data.amenities;
        if (typeof amenities === "string") {
          amenities = amenities
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean);
        }
        setProperty({ ...data, amenities });
      })
      .catch((err) => {
        // SESSION_EXPIRED means AuthContextWeb has already cleared auth
        // state, which will make ProtectedRoute redirect to login on its
        // own re-render — don't flash an error page in the meantime.
        if (active && err?.message !== "SESSION_EXPIRED") {
          setError(err?.message || "Failed to load property details.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (!shareFeedback) return;
    const timer = setTimeout(() => setShareFeedback(""), 2500);
    return () => clearTimeout(timer);
  }, [shareFeedback]);

  useEffect(() => {
    const imgs = Array.isArray(property?.images) ? property.images : [];

    if (!imgs.length) return;

    if (activeImg >= imgs.length) {
      setActiveImg(0);
      return;
    }

    if (imgs.length < 2) return;

    const nextIndex = (activeImg + 1) % imgs.length;
    const prevIndex = (activeImg - 1 + imgs.length) % imgs.length;

    [nextIndex, prevIndex].forEach((idx) => {
      const url = imgs[idx]?.url;
      if (!url) return;
      const preloadImage = new Image();
      preloadImage.decoding = "async";
      preloadImage.src = url;
    });
  }, [property, activeImg]);

  const handleToggleStatus = async () => {
    if (!property || statusUpdating) return;
    setStatusUpdating(true);
    const prevStatus = property.status;
    const newStatus = prevStatus === "AVAILABLE" ? "INACTIVE" : "AVAILABLE";
    setProperty((p) => ({ ...p, status: newStatus }));
    try {
      await apiRequest(`/properties/${id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
    } catch {
      setProperty((p) => ({ ...p, status: prevStatus }));
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDeleteProperty = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE" || !property)
      return;
    setDeleting(true);
    setDeleteError("");
    try {
      await apiRequest(`/properties/${id}`, { method: "DELETE" });
      navigate("/owner/listings");
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete property.");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <main className="detail-page detail-page--bare">
        <p className="loading-message">Loading property…</p>
      </main>
    );
  }

  if (error || !property) {
    return (
      <main className="detail-page detail-page--bare">
        <p className="form-error">{error || "Property not found."}</p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() =>
            navigate(
              ["OWNER", "ADMIN", "SUPER_ADMIN"].includes(
                String(user?.role || "").toUpperCase(),
              )
                ? "/owner/listings"
                : "/app/search",
            )
          }
        >
          Back
        </button>
      </main>
    );
  }

  const images =
    Array.isArray(property.images) && property.images.length > 0
      ? property.images
      : null;

  const isOwner =
    ["OWNER", "ADMIN", "SUPER_ADMIN"].includes(
      String(user?.role || "").toUpperCase(),
    ) &&
    property != null &&
    String(property.ownerId) === String(user?.userId);

  const lat = parseFloat(property.latitude);
  const lng = parseFloat(property.longitude);
  const hasMap = isFinite(lat) && isFinite(lng);

  const googleMapsKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const mapSrc =
    hasMap && googleMapsKey
      ? `https://www.google.com/maps/embed/v1/place` +
        `?key=${googleMapsKey}` +
        `&q=${lat},${lng}` +
        `&zoom=16`
      : null;

  const googleMapsLink = hasMap
    ? `https://www.google.com/maps?q=${lat},${lng}`
    : null;

  const amenities =
    Array.isArray(property.amenities) && property.amenities.length > 0
      ? property.amenities
      : null;

  const infoRows = [
    [BsHouseDoor, "Type", humanize(property.propertyType)],
    [BsColumnsGap, "Furnishing", humanize(property.furnishedStatus)],
    [
      BsLayers,
      "Floor",
      property.floorLevel != null
        ? `${property.floorLevel} of ${fmt(property.totalFloors)}`
        : null,
    ],
    [
      BsArrowsAngleExpand,
      "Area",
      property.area != null ? `${property.area} m²` : null,
    ],
    [
      BsCalendar3,
      "Lease",
      property.leaseDuration != null ? `${property.leaseDuration} mo` : null,
    ],
    [BsCalendarCheck, "Available", fmtDate(property.availableFrom)],
    [
      BsCashCoin,
      "Deposit",
      property.deposit != null ? asCurrency(property.deposit) : null,
    ],
    [
      BsTools,
      "Maintenance",
      property.maintenanceFee != null
        ? asCurrency(property.maintenanceFee)
        : null,
    ],
    [BsCarFront, "Parking", fmtBool(property.parkingAvailable)],
    [BsHeart, "Pets", humanize(property.petPolicy)],
    [BsDoorOpen, "Balcony", fmtBool(property.balcony)],
    [BsDroplet, "Pool", fmtBool(property.swimmingPool)],
    [BsLightning, "Gym", fmtBool(property.gym)],
    [BsThermometerHalf, "Heating", fmtBool(property.heating)],
    [BsSnow, "A/C", fmtBool(property.airConditioning)],
    [
      BsShieldCheck,
      "Security",
      property.securityFeatures ? fmt(property.securityFeatures) : null,
    ],
    [BsToggle2On, "Status", humanize(property.status)],
    [
      BsEnvelope,
      "Postal Code",
      property.postalCode ? fmt(property.postalCode) : null,
    ],
    [
      BsPinMap,
      "Neighborhood",
      property.neighborhoodInfo ? fmt(property.neighborhoodInfo) : null,
    ],
  ].filter(([, , val]) => val !== null && val !== "—" && val !== undefined);

  return (
    <div className="detail-page">
      {/* ── Back bar ── */}
      <div className="detail-back-bar">
        <button
          className="detail-back-btn"
          type="button"
          onClick={() => navigate(-1)}
        >
          <BsArrowLeft className="detail-back-arrow" aria-hidden="true" />
          Home
        </button>
        <span className="detail-back-title" title={property.title}>
          {property.title}
        </span>
      </div>

      {/* ── Two-column body ── */}
      <div className="detail-body">
        {/* LEFT: gallery + map */}
        <div className="detail-left">
          <div className="detail-gallery">
            {images ? (
              <>
                <div className="detail-gallery-main">
                  <img
                    src={images[activeImg]?.url}
                    alt={property.title || "Property"}
                    className="detail-gallery-hero"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>
                {images.length > 1 && (
                  <div className="detail-gallery-thumbs">
                    {images.map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`detail-thumb-btn${
                          i === activeImg ? " active" : ""
                        }`}
                        onClick={() => setActiveImg(i)}
                      >
                        <img
                          src={img.url}
                          alt={`View ${i + 1}`}
                          className="detail-thumb-img"
                          loading={i < EAGER_THUMB_COUNT ? "eager" : "lazy"}
                          decoding="async"
                          fetchPriority={i < EAGER_THUMB_COUNT ? "high" : "low"}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="detail-no-image">📷 No images uploaded</div>
            )}
          </div>

          {(mapSrc || googleMapsLink) && (
            <div className="detail-map-wrap">
              <p className="detail-map-label">
                {property.location || property.city || "Location"}
              </p>
              {mapSrc ? (
                <iframe
                  title="Property location map"
                  src={mapSrc}
                  className="detail-map-iframe"
                  loading="lazy"
                  allowFullScreen
                />
              ) : null}
              {googleMapsLink && (
                <a
                  href={googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="detail-map-link"
                >
                  View on Google Maps
                </a>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: all details */}
        <div className="detail-right">
          <h1 className="detail-title">
            {property.title || "Untitled property"}
          </h1>
          <p className="detail-location">
            {property.location || property.city || "Location unavailable"}
          </p>

          <div className="detail-tag-row">
            {property.price != null && (
              <span className="detail-tag detail-tag--price">
                {asCurrency(property.price)}
              </span>
            )}
            {property.bedrooms != null && (
              <span className="detail-tag">{property.bedrooms} bed</span>
            )}
            {property.bathrooms != null && (
              <span className="detail-tag">{property.bathrooms} bath</span>
            )}
            {property.propertyType && (
              <span className="detail-tag">
                {humanize(property.propertyType)}
              </span>
            )}
          </div>

          {property.description && (
            <div className="detail-description-section">
              <p className="detail-description-label">Description</p>
              <p className="detail-description">{property.description}</p>
            </div>
          )}

          {/* ── Contact Card ── */}
          <div className="detail-contact-card">
            <div className="detail-contact-price">
              {property.price != null
                ? asCurrency(property.price)
                : "Price on request"}
              {property.price != null && (
                <span className="detail-contact-price-per"> / month</span>
              )}
            </div>
            {isOwner ? (
              <div className="detail-owner-actions">
                {/* ── Horizontal button row ── */}
                <div className="detail-owner-btn-row">
                  <button
                    type="button"
                    className="detail-btn-primary"
                    onClick={() => navigate(`/owner/edit-property/${id}`)}
                  >
                    <BsPencil aria-hidden="true" />
                    Edit Property
                  </button>
                  <button
                    type="button"
                    className="detail-btn-secondary"
                    onClick={handleToggleStatus}
                    disabled={statusUpdating}
                  >
                    <BsEyeSlash aria-hidden="true" />
                    {statusUpdating
                      ? "Updating…"
                      : property.status === "AVAILABLE"
                        ? "Mark Inactive"
                        : "Mark Available"}
                  </button>
                  <button
                    type="button"
                    className="detail-btn-secondary detail-share-btn"
                    onClick={handleShareProperty}
                  >
                    <BsShare aria-hidden="true" />
                    Share
                  </button>
                  {!showDeleteConfirm && (
                    <button
                      type="button"
                      className="detail-btn-ghost-danger"
                      onClick={() => {
                        setDeleteConfirmText("");
                        setDeleteError("");
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <BsTrash aria-hidden="true" />
                      Delete Property
                    </button>
                  )}
                </div>

                {/* ── Delete confirm panel ── */}
                {showDeleteConfirm && (
                  <div className="detail-delete-confirm">
                    <p className="acct-delete-warning">
                      Type <strong>DELETE</strong> to confirm permanent removal.
                    </p>
                    <input
                      className="detail-delete-input"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      autoFocus
                    />
                    {deleteError && <p className="form-error">{deleteError}</p>}
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        type="button"
                        className="detail-btn-ghost-danger"
                        onClick={handleDeleteProperty}
                        disabled={
                          deleting ||
                          deleteConfirmText.trim().toUpperCase() !== "DELETE"
                        }
                      >
                        {deleting ? "Deleting…" : "Confirm"}
                      </button>
                      <button
                        type="button"
                        className="detail-btn-secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="detail-contact-btn-wrap">
                <button
                  type="button"
                  className="detail-contact-btn"
                  onClick={handleMessageOwner}
                  disabled={msgLoading}
                >
                  <BsChatDots aria-hidden="true" />
                  {msgLoading ? "Opening chat…" : "Message Owner"}
                </button>
                <button
                  type="button"
                  className="detail-btn-secondary detail-share-btn"
                  onClick={handleShareProperty}
                >
                  <BsShare aria-hidden="true" />
                  Share Property
                </button>
                {msgError && (
                  <p
                    className="detail-contact-note"
                    style={{ color: "#dc2626" }}
                  >
                    {msgError}
                  </p>
                )}
                {shareFeedback && (
                  <p className="detail-contact-note">{shareFeedback}</p>
                )}
              </div>
            )}

            {isOwner && shareFeedback && (
              <p className="detail-contact-note">{shareFeedback}</p>
            )}
          </div>

          <div className="detail-features-card">
            <h3 className="detail-features-heading">Features &amp; Details</h3>
            <div className="detail-info-grid">
              {infoRows.map(([Icon, label, value]) => (
                <div className="detail-info-cell" key={label}>
                  <span className="detail-info-label">
                    <Icon className="detail-info-icon" aria-hidden="true" />
                    {label}
                  </span>
                  <span className="detail-info-value">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {amenities && (
            <div className="detail-amenities-section">
              <h3 className="detail-section-heading">Amenities</h3>
              <div className="detail-amenities-list">
                {amenities.map((a) => (
                  <span key={a} className="detail-amenity-chip">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
